import { formCTAurl, SigninRefPath, CTApath } from '@ecs/tng-common/scripts/registration';
import { ifCRMcampaign } from './analytics';
import { getAllParams } from './functions';

export type DefaultCTAComponentProps = {
  dataOp?: string;
  category?: string;
  ctaCustom?: string;
  articleId?: string;
  categoryId?: string | number;
  buttonCTAclick?: () => void;
};

interface ICTAcomponentsParams {
  customCta?: string;
  pc?: string | null;
  cc?: string | null;
  mkid?: string | null;
  ty?: string | null;
  category?: string;
  categoryId?: string | number | null;
  articleId?: string;
  placement: string;
}
type ctaType =
  | 'ad_boost'
  | 'ad_default_freereport'
  | 'ad_freescore'
  | 'ad_dispute'
  | 'ad_default_idp'
  | 'ad_freeze'
  | 'ad_fraudalert'
  | 'ad_default_exp_go'
  | 'ad_billfixer'
  | 'ad_default_ro_debt_debtconsolidation'
  | 'ad_default_ownup'
  | 'ad_default_ro_card'
  | 'ad_default_ro_card_interest'
  | 'ad_default_ro_card_transfer'
  | 'ad_default_ro_card_rewards'
  | 'ad_default_ro_card_students'
  | 'ad_default_ro_card_secured'
  | 'ad_default_ro_card_travel'
  | 'ad_default_ro_loan'
  | 'ad_default_ro_loan_personal'
  | 'ad_default_ins_plai'
  | 'ad_default_ladderlife'
  | 'ad_default_fundera'
  | 'ad_default_darkweb'
  | 'ad_default_ro_main'
  | 'ad_default_mobile_app'
  | 'ad_crm_mc'
  | 'ad_crm_plall'
  | 'ad_crm_cmgeneric'
  | 'ad_banking_cd'
  | 'ad_banking_mma'
  | 'ad_banking_hys';

// helper functions
export const ifCustomCTARestriction = (customCta: string | undefined) => {
  // do not set any CTA values if customCta is none
  return customCta === 'none';
};

export function ifCRMcampaignIsSet(pc: string | null, ty: string | null) {
  return ifCRMcampaign(pc) && ty;
}
function getCrmUrl(params: ICTAcomponentsParams) {
  if (params.mkid && params.mkid.length === 36) {
    let value = `${process.env.CORVETTE_HOST}/myOffers/cards?m=${params.mkid}`;
    if (params.pc) {
      value += `&pc=${params.pc}`;
    }
    if (params.cc) {
      value += `&cc=${params.cc}`;
    }
    return value;
  }
  return formCTAurl(
    {
      refUrl: SigninRefPath.creditCards,
      skip: 'int',
      m: params.mkid
    },
    CTApath.loginIndex
  );
}
const setCTAidentifier = (params: ICTAcomponentsParams) => {
  const articleidValue = params.articleId ? `-s${params.articleId}` : '';
  return `-c${params.categoryId}${articleidValue}`;
};
const setCTAplacement = (params: ICTAcomponentsParams) => {
  const { placement } = params;
  if (ifCRMcampaign(params.pc)) {
    if (params.ty) {
      return `${placement}-crm-${params.ty}`;
    }
    return `${placement}-crm`;
  }
  return placement;
};
const replaceOriginValues = (str: string, params: ICTAcomponentsParams) => {
  return str
    .replace('[ctaIdentifier]', setCTAidentifier(params))
    .replace('[ctaPlacement]', setCTAplacement(params));
};
const getFunderaQuery = (params: ICTAcomponentsParams) => {
  let value = '';
  if (params.category) {
    value = `&id=${params.category}`;
  }
  if (params.articleId) {
    value += `:${params.articleId}`;
  }
  if (params.pc) {
    value += `&pc=${params.pc}`;
  }
  if (params.cc) {
    value += `&cc=${params.cc}`;
  }
  if (params.mkid) {
    value += `&mkid=${params.mkid}`;
  }
  return value;
};
const getLadderLifeQuery = (params: ICTAcomponentsParams) => {
  let value = '';
  if (params.categoryId) {
    value += `&lddr_name=${params.categoryId}`;
    if (params.articleId) {
      value += `.${params.articleId}`;
    }
  }
  if (params.cc) {
    value += `&lddr_creative=${params.cc}`;
    if (params.mkid) {
      value += `.${params.mkid}`;
    }
  }
  if (params.pc) {
    value += `&lddr_medium=${params.pc}`;
  }
  return value;
};
const getOwnupQuery = (params: ICTAcomponentsParams) => {
  let value = '';
  if (params.pc) {
    value += `&utm_medium=${params.pc}`;
    if (params.cc) {
      value += `:${params.cc}`;
    }
  }
  if (params.category) {
    value += `&utm_term=${params.category}`;
    if (params.articleId) {
      value += `:${params.articleId}`;
    }
    if (params.mkid) {
      value += `:${params.mkid}`;
    }
  }
  return value;
};
export const categoryMapping = (slug: string) => {
  const mapping: { [key: string]: ctaType } = {
    home: 'ad_boost',
    banking: 'ad_banking_hys',
    cd: 'ad_banking_cd',
    'checking-account': 'ad_banking_hys',
    'money-market': 'ad_banking_mma',
    'saving-account': 'ad_banking_hys',
    'credit-advice': 'ad_default_freereport',
    'improve-credit': 'ad_boost',
    'report-advice': 'ad_default_freereport',
    bankruptcy: 'ad_boost',
    'charge-off': 'ad_boost',
    'closed-accounts': 'ad_default_freereport',
    collections: 'ad_boost',
    cosigning: 'ad_default_freereport',
    'credit-limit': 'ad_freescore',
    'credit-repair': 'ad_boost',
    'denied-credit': 'ad_boost',
    dispute: 'ad_dispute',
    foreclosure: 'ad_boost',
    inquiry: 'ad_boost',
    'late-payment': 'ad_boost',
    'negative-information': 'ad_boost',
    'report-details': 'ad_default_freereport',
    repossession: 'ad_boost',
    'score-advice': 'ad_freescore',
    'score-guide': 'ad_freescore',
    'death-of-a-relative': 'ad_default_freereport',
    divorce: 'ad_default_freereport',
    employment: 'ad_default_freereport',
    'establishing-credit': 'ad_freescore',
    kids: 'ad_default_freereport',
    marriage: 'ad_default_freereport',
    students: 'ad_default_exp_go',
    'fraud-and-identity-theft': 'ad_default_idp',
    cybersecurity: 'ad_default_darkweb',
    'data-breach': 'ad_default_idp',
    'fraud-alert': 'ad_fraudalert',
    prevention: 'ad_default_idp',
    'security-freeze': 'ad_freeze',
    'personal-finance': 'ad_freescore',
    budgeting: 'ad_billfixer',
    saving: 'ad_billfixer',
    debt: 'ad_default_ro_debt_debtconsolidation',
    investing: 'ad_freescore',
    retirement: 'ad_freescore',
    taxes: 'ad_freescore',
    mortgage: 'ad_default_ownup',
    'mortgage-basics': 'ad_default_ownup',
    'rates-and-affordability': 'ad_default_ownup',
    rentals: 'ad_boost',
    'credit-cards': 'ad_default_ro_card',
    apr: 'ad_default_ro_card_interest',
    'balance-transfers': 'ad_default_ro_card_transfer',
    'best-credit-cards': 'ad_default_ro_card',
    'cash-back-credit-cards': 'ad_default_ro_card_rewards',
    'credit-card-basics': 'ad_default_ro_card',
    'credit-card-reviews': 'ad_default_ro_card',
    'rewards-credit-cards': 'ad_default_ro_card_rewards',
    'student-credit-cards': 'ad_default_ro_card_students',
    'secured-credit-cards': 'ad_default_ro_card_secured',
    'travel-credit-cards': 'ad_default_ro_card_travel',
    loans: 'ad_default_ro_loan',
    'auto-loans': 'ad_freescore',
    'best-loans': 'ad_default_ro_loan',
    'loan-basics': 'ad_default_ro_loan',
    'loan-reviews': 'ad_default_ro_loan',
    'personal-loans': 'ad_default_ro_loan_personal',
    insurance: 'ad_default_ins_plai',
    'auto-insurance': 'ad_default_ins_plai',
    'home-insurance': 'ad_freescore',
    'life-insurance': 'ad_default_ladderlife',
    'health-insurance': 'ad_freescore',
    'pet-insurance': 'ad_freescore',
    'student-loans': 'ad_freescore',
    'insurance-reviews': 'ad_default_ins_plai',
    'small-business': 'ad_default_fundera',
    'news-trends': 'ad_default_freereport',
    products: 'ad_default_freereport',
    research: 'ad_default_freereport',
    calculators: 'ad_freescore'
  };
  return mapping[slug];
};
export type IConfCTAValuesBottom = {
  promoIcon: string;
  promoHeading: string;
  promoSubHeading: string;
  promoBtnCopy: string;
  promoSubBtnText?: string;
};
export type IConfCTAValuesSticky = {
  stickyHeading: string;
  stickySubCopy?: string;
  stickyBtnCopy: string;
};
export type IConfCTAValuesCommon = {
  dataLinkAttr: string;
  btnLink: string;
  dataOp?: string;
};
export type IConfCTAValues = IConfCTAValuesBottom & IConfCTAValuesSticky & IConfCTAValuesCommon;

export const confCTAValues = (value: ctaType, params: ICTAcomponentsParams): IConfCTAValues => {
  const mapping: Record<ctaType, IConfCTAValues> = {
    ad_boost: {
      stickyHeading: 'Boost your FICO<sup>®</sup> Score instantly for free',
      stickyBtnCopy: 'Start your boost',
      btnLink: '',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-boost[ctaIdentifier]',
      promoIcon: 'icon-boost-rocket',
      promoHeading: 'Instantly raise your FICO<sup>®</sup> Score for free',
      promoSubHeading:
        'Use Experian Boost<sup>®</sup> to get credit for the bills you already pay like utilities, mobile phone, video streaming services and now rent.',
      promoBtnCopy: 'Start your boost',
      promoSubBtnText: 'No credit card required'
    },
    ad_default_freereport: {
      stickyHeading: 'Check your credit report for free',
      stickyBtnCopy: 'Get started',
      btnLink: formCTAurl({
        offer: 'at_frsas119',
        authType: '102'
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-frcr[ctaIdentifier]',
      promoIcon: 'icon-score',
      promoHeading: 'What’s on your credit report?',
      promoSubHeading:
        'Stay up to date with your latest credit information—and get your FICO<sup>®</sup> Score for free.',
      promoBtnCopy: 'Get your free report',
      promoSubBtnText: 'No credit card required'
    },
    ad_freescore: {
      stickyHeading: 'Check your FICO<sup>®</sup> Score for free',
      stickyBtnCopy: 'Get started',
      btnLink: formCTAurl({
        offer: 'at_frsas119',
        authType: '102',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-frsc[ctaIdentifier]',
      promoIcon: 'icon-score',
      promoHeading: 'What makes a good credit score?',
      promoSubHeading:
        'Learn what it takes to achieve a good credit score. Review your FICO<sup>®</sup> Score for free and see what’s helping and hurting your score.',
      promoBtnCopy: 'Get your FICO<sup>®</sup> Score',
      promoSubBtnText: 'No credit card required'
    },
    ad_banking_hys: {
      stickyHeading: 'Find the best high-yield savings accounts',
      stickyBtnCopy: 'Compare accounts',
      btnLink: '/blogs/ask-experian/best-high-yield-savings-accounts/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-banking-hys[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-piggy-bank',
      promoHeading: 'Earn more with a high-yield savings account',
      promoSubHeading:
        'Make your money work harder with a high-yield savings account—earn higher returns with easy access to your funds.',
      promoBtnCopy: 'Compare accounts'
    },
    ad_banking_cd: {
      stickyHeading: 'Find the best CD rates',
      stickyBtnCopy: 'Compare rates',
      btnLink: '/blogs/ask-experian/best-cd-rates/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-banking-cd[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-certificate',
      promoHeading: 'Grow your money safely with a CD',
      promoSubHeading:
        'Lock in savings with a certificate of deposit—earn higher interest rates over a fixed term.',
      promoBtnCopy: 'Compare accounts'
    },
    ad_banking_mma: {
      stickyHeading: 'Find the best money market accounts',
      stickyBtnCopy: 'Compare accounts',
      btnLink: '/blogs/ask-experian/best-money-market-accounts/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-banking-mma[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-dollar',
      promoHeading: 'Earn more with a money market account',
      promoSubHeading:
        'Maximize your savings with a money market account—enjoy higher interest rates and easy access to your funds.',
      promoBtnCopy: 'Compare accounts'
    },
    ad_default_ro_card_interest: {
      stickyHeading: 'Find low interest cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards + '?features=lowinterestrate',
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-interest[ctaIdentifier]',
      promoIcon: 'icon-credit-card',
      promoHeading: 'Discover low interest credit cards',
      promoSubHeading:
        'Keep more cash in your wallet with a low interest credit card. See what offers you qualify for based on your FICO<sup>®</sup> Score.',
      promoBtnCopy: 'See your offers'
    },
    ad_dispute: {
      stickyHeading: 'File a dispute for free',
      stickyBtnCopy: 'Get started',
      btnLink: '/disputes/main.html',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-dispute[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-file-check',
      promoHeading: 'Dispute information on your credit report',
      promoSubHeading:
        'Found information on your Experian credit report you feel is innacurate? File a dispute for free to have it resolved.',
      promoBtnCopy: 'Get started'
    },
    ad_default_idp: {
      stickyHeading: 'Monitor your credit for free',
      stickyBtnCopy: 'Get free monitoring',
      btnLink: formCTAurl({
        offer: 'at_frsas119',
        authType: '102',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-frsc[ctaIdentifier]',
      promoIcon: 'icon-monitor',
      promoHeading: 'Monitor your credit for free',
      promoSubHeading:
        'Credit monitoring can help you detect possible identity fraud, and can prevent surprises when you apply for credit. Get daily notifications when changes are detected.',
      promoBtnCopy: 'Get free monitoring'
    },
    ad_freeze: {
      stickyHeading: 'Freeze your credit file for free',
      stickyBtnCopy: 'Create a free account',
      btnLink: formCTAurl(
        {
          cname: 'FREEZE',
          proType: 'prefill',
          offer: 'at_frsas122,at_ltdreg102',
          it: 'frz'
        },
        CTApath.mobileMatchGetStarted
      ),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-frz[ctaIdentifier]',
      promoIcon: 'icon-freeze-experian',
      promoHeading: 'Freeze your credit file for free',
      promoSubHeading:
        'A security freeze, often known as a credit freeze, limits access to your Experian credit report—helping protect you against identity theft.',
      promoBtnCopy: 'Create a free account'
    },
    ad_fraudalert: {
      stickyHeading: 'Add a fraud alert to your credit file',
      stickyBtnCopy: 'Get started',
      btnLink: '/help/fraud-alert/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-fraudalert[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-protection-shield',
      promoHeading: 'Have you been a victim of identity theft?',
      promoSubHeading:
        'Add another layer of protection to your credit by notifying lenders to take extra steps to verify your identity before processing new credit.',
      promoBtnCopy: 'Add a fraud alert'
    },
    ad_default_exp_go: {
      stickyHeading: 'Establish and build your credit for free',
      stickyBtnCopy: 'Jump-start your credit',
      btnLink: '/credit/experian-go/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-go[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-credit-score',
      promoHeading: 'Establish and build your credit for free',
      promoSubHeading:
        'Create your Experian credit report with Experian Go<sup>TM</sup>. We’ll help you find your best path to credit and your first FICO<sup>®</sup> Score.',
      promoBtnCopy: 'Jump-start your credit'
    },
    ad_billfixer: {
      stickyHeading: 'Save money on bills and subscriptions',
      stickyBtnCopy: 'Get started',
      btnLink: '/money/manage-bills-subscriptions/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-billfixer[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-wallet',
      promoHeading: 'Want to lower your monthly bills?',
      promoSubHeading: 'We’ll negotiate bills for you and cancel unwanted subscriptions.',
      promoBtnCopy: 'Get started'
    },
    ad_default_ro_debt_debtconsolidation: {
      stickyHeading: 'See a free summary of your debt',
      stickyBtnCopy: 'Review your credit',
      btnLink: formCTAurl({
        offer: 'at_frsas119',
        authType: '102',
        refUrl: SigninRefPath.creditHub,
        sem: '1'
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-debt-consolidation[ctaIdentifier]',
      promoIcon: 'icon-score',
      promoHeading: 'Find out what debts you owe',
      promoSubHeading:
        'Your free credit report lists all your debts, such as credit card balances and loans, helping you create a plan to tackle your debt and improve your financial health.',
      promoBtnCopy: 'Review your credit'
    },
    ad_default_ownup: {
      stickyHeading: 'Shop mortgage rates with a few clicks',
      stickySubCopy: 'On Ownup.com (NMLS# 1450805)',
      stickyBtnCopy: 'Learn more',
      btnLink: `https://lp.ownup.com/experian/?utm_source=experian&utm_campaign=editorial&utm_content=educational${getOwnupQuery(params)}`,
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ownup[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-mortgage-refi',
      promoHeading: 'Curious about your mortgage options?',
      promoSubHeading:
        'Explore personalized solutions from multiple lenders and make informed decisions about your home financing. Leverage expert advice to see if you can save thousands of dollars.',
      promoBtnCopy: 'Learn more'
    },
    ad_default_ro_card: {
      stickyHeading: 'Compare credit cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card[ctaIdentifier]',
      promoIcon: 'icon-card-praise',
      promoHeading: 'Don’t apply blindly',
      promoSubHeading:
        'Apply for credit cards confidently with personalized offers based on your credit profile. Get started with your FICO<sup>®</sup> Score for free.',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_card_transfer: {
      stickyHeading: 'Balance transfer cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards + '?features=balancetransferallowed',
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-transfer[ctaIdentifier]',
      promoIcon: 'icon-balance-transfer',
      promoHeading: 'Best balance transfer cards',
      promoSubHeading:
        'Need to consolidate debt and save on interest? See if you qualify for intro offers like 0% intro APR up to 21 months based on your FICO<sup>®</sup> Score.',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_card_rewards: {
      stickyHeading: 'Compare rewards cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards + '?rewards=Points',
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-reward[ctaIdentifier]',
      promoIcon: 'icon-new-item-offer',
      promoHeading: 'Get the rewards you deserve',
      promoSubHeading:
        'Cash back? Miles? Points? Whatever suits your goals, we can help match you to personalized rewards card offers. Start with your FICO<sup>®</sup> Score for free.',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_card_students: {
      stickyHeading: 'Compare student cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-students[ctaIdentifier]',
      promoIcon: 'icon-student-education',
      promoHeading: 'Credit cards for students',
      promoSubHeading:
        'Looking to build your credit? Browse credit cards and secured credit cards designed for students. Get started with your FICO<sup>®</sup> Score. for free',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_card_secured: {
      stickyHeading: 'Compare secured cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-secured[ctaIdentifier]',
      promoIcon: 'icon-credit-card',
      promoHeading: 'Looking to build credit?',
      promoSubHeading:
        'Discover secured credit card offers matched to you, so you can apply with confidence. Get started with your FICO<sup>®</sup> Score for free.',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_card_travel: {
      stickyHeading: 'Compare travel cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card-travel[ctaIdentifier]',
      promoIcon: 'icon-travel',
      promoHeading: 'Get the travel rewards you deserve',
      promoSubHeading:
        'Earn miles and find offers with bonuses and perks for your travel needs. Start with your FICO<sup>®</sup> Score and get matched with personalized rewards card offers.',
      promoBtnCopy: 'See your offers'
    },
    ad_default_ro_loan: {
      stickyHeading: 'Find the right loan for you',
      stickyBtnCopy: 'See your loan options',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.personalLoans,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-loan[ctaIdentifier]',
      promoIcon: 'icon-bank',
      promoHeading: 'Is it time for a loan?',
      promoSubHeading:
        'Shopping for a car or have last minute expenses? Compare loan offers matched to your credit profile for free.',
      promoBtnCopy: 'See your loan options'
    },
    ad_default_ro_loan_personal: {
      stickyHeading: 'Find the right personal loan for you',
      stickyBtnCopy: 'Get matched for free',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.personalLoans,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-loan-personal[ctaIdentifier]',
      promoIcon: 'icon-bank',
      promoHeading: 'Need a personal loan?',
      promoSubHeading:
        'Need to eliminate debt or access cash fast? Compare personal loan offers matched to your credit profile for free.',
      promoBtnCopy: 'See your loan options'
    },
    ad_default_ins_plai: {
      stickyHeading: 'Stop overpaying for car insurance',
      stickyBtnCopy: 'Find savings',
      btnLink: '/insurance/car-insurance-quotes/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ins-plai[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-auto',
      promoHeading: 'Don’t overpay for auto insurance',
      promoSubHeading:
        'If you’re looking for ways to cut back on monthly costs, it could be a good idea to see if you can save on your auto insurance.',
      promoBtnCopy: 'Find savings'
    },
    ad_default_ladderlife: {
      stickyHeading: 'Free life insurance quote',
      stickySubCopy:
        'On LadderLife.com | <a href="https://www.ladderlife.com/terms?lddr_source=experian&utm_source=experian" target="_blank">See terms</a>',
      stickyBtnCopy: 'Get your quote',
      btnLink: `https://www.ladderlife.com/experian?lddr_source=experian$${getLadderLifeQuery(params)}`,
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ladderlife[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-life-insurance',
      promoHeading: 'Check your life insurance price for free',
      promoSubHeading: 'Get your term life insurance quote online in minutes.',
      promoBtnCopy: 'See your price'
    },
    ad_default_fundera: {
      stickyHeading: 'Fund your small business',
      stickySubCopy: 'On Fundera.com',
      stickyBtnCopy: 'Explore my options',
      btnLink: `https://www.fundera.com/partner_landing?aid=experian${getFunderaQuery(params)}`,
      dataLinkAttr: 'ae-cta-[ctaPlacement]-fundera[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-employment',
      promoHeading: 'Bankroll your big idea with Fundera by NerdWallet',
      promoSubHeading:
        'Get matched with the right small business financing product and a dedicated personal funding advisor—for free.',
      promoBtnCopy: 'Learn more'
    },
    ad_default_darkweb: {
      stickyHeading: 'Is your info on the dark web?',
      stickyBtnCopy: 'Run a free scan',
      btnLink: '/protection/free-dark-web-email-scan/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-darkweb[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-dark-web',
      promoHeading: 'Is your info on the dark web?',
      promoSubHeading:
        'Internet criminals buy and sell personal data on the dark web to commit fraud. Could they have your info? Find out with a free Dark Web Scan.',
      promoBtnCopy: 'Run a free scan'
    },
    ad_default_ro_main: {
      stickyHeading: 'Compare credit cards matched to you',
      stickyBtnCopy: 'See your cards',
      btnLink: formCTAurl({
        offer: 'at_frsas109',
        authType: '102',
        refUrl: SigninRefPath.creditCards,
        sem: '1',
        showDisclaimer: true
      }),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-ro-card[ctaIdentifier]',
      promoIcon: 'icon-card-praise',
      promoHeading: 'Don’t apply blindly',
      promoSubHeading:
        'Apply for credit cards and loans confidently with personalized offers based on your credit profile. Get started with your FICO<sup>®</sup> Score for free.',
      promoBtnCopy: 'Shop personalized offers'
    },
    ad_default_mobile_app: {
      stickyHeading: 'Take your credit with you',
      stickyBtnCopy: 'Get the app',
      btnLink: '/credit/experian-app/',
      dataLinkAttr: 'ae-cta-[ctaPlacement]-app[ctaIdentifier]',
      dataOp: '',
      promoIcon: 'icon-experian-phone',
      promoHeading: 'Take your credit with you',
      promoSubHeading:
        'Download our free app to access your Experian credit report & FICO<sup>®</sup> Score on the go!',
      promoBtnCopy: 'Get the app'
    },
    ad_crm_mc: {
      stickyHeading: 'Stay up to date on your credit',
      stickyBtnCopy: 'Sign in',
      btnLink: formCTAurl(
        {
          refUrl: SigninRefPath.creditReport,
          ...getAllParams()
        },
        CTApath.loginIndex
      ),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-login[ctaIdentifier]',
      promoIcon: 'icon-new-credit',
      promoHeading: 'Stay up-to-date',
      promoSubHeading:
        'Info in your credit report gets frequently added, updated or deleted. Have there been any changes to your credit report lately?',
      promoBtnCopy: 'Sign in to find out'
    },
    ad_crm_plall: {
      stickyHeading: 'You deserve some credit',
      stickyBtnCopy: 'Explore your options',
      btnLink: formCTAurl(
        {
          refUrl: SigninRefPath.personalLoans,
          ...getAllParams()
        },
        CTApath.loginIndex
      ),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-login[ctaIdentifier]',
      promoIcon: 'icon-bank',
      promoHeading: 'Your loan options await',
      promoSubHeading:
        'The right loan can help you reach your goals faster, so we’ve matched the best-of-class just for you. Sign in and compare loans based on the rewards and features that matter most to you.',
      promoBtnCopy: 'Explore your options'
    },
    ad_crm_cmgeneric: {
      stickyHeading: 'You deserve some credit',
      stickyBtnCopy: 'Explore your options',
      btnLink: getCrmUrl(params),
      dataLinkAttr: 'ae-cta-[ctaPlacement]-login[ctaIdentifier]',
      promoIcon: 'icon-credit-money-check',
      promoHeading: 'You deserve some credit',
      promoSubHeading:
        'Shop personalized credit card and loan offers based on your FICO<sup>®</sup> Score.',
      promoBtnCopy: 'Explore your options'
    }
  };
  const ctaData = mapping[value] || mapping.ad_default_freereport;
  ctaData.dataLinkAttr = replaceOriginValues(ctaData.dataLinkAttr, params);
  return ctaData;
};

const getCustomCtaAdDisplay = (customCta: string) => {
  const customCtaMap: { [key: string]: ctaType } = {
    boost: 'ad_boost',
    creditmatch: 'ad_default_ro_main',
    monitoring: 'ad_default_idp',
    mobileapp: 'ad_default_mobile_app',
    billfixer: 'ad_billfixer'
  };

  return customCtaMap[customCta];
};
const getCrmAdDisplay = (ty: string) => {
  const tyMap: { [key: string]: ctaType } = {
    mc: 'ad_crm_mc',
    plall: 'ad_crm_plall',
    ccpq: 'ad_crm_cmgeneric'
  };

  return tyMap[ty];
};
// the main logic function
export const returnCTAcomponentsData = ({
  customCta,
  pc,
  ty,
  cc,
  mkid,
  category,
  categoryId,
  articleId,
  placement
}: ICTAcomponentsParams): IConfCTAValues => {
  // set default value
  let ctaTypeValue: ctaType = 'ad_default_freereport';
  if (customCta) {
    ctaTypeValue = getCustomCtaAdDisplay(customCta);
  } else if (ifCRMcampaign(pc) && ty) {
    ctaTypeValue = getCrmAdDisplay(ty);
  } else if (category) {
    ctaTypeValue = categoryMapping(category);
  }
  return confCTAValues(ctaTypeValue, {
    pc,
    cc,
    mkid,
    category,
    articleId,
    categoryId,
    placement
  });
};
